import { useMemo } from "react";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { usePredictCostBatch } from "../../../../hooks/usePredictCost";
import { buildCostPrediction } from "../../../../utils/costPrediction";
import type { BaseRequest, Job, Nurse, RequestResponsesStatus } from "../../../../utils/models";
import { getRespondedRequestData } from "../../../../utils/utilFunctions";
import { isAgencyNurse } from "../../../../utils/warningDialogs/utils";
import { useRequestUtils } from "../../../RequestData/Provider/useRequestUtils";
import { GapsPredictCostSource, useReportPredictCostFailure } from "../useReportPredictCostFailure";
import { applicantJob, type GapsAcceptedHours, type GapsOtherShiftThatDay, otherShiftThatDay, shiftsOnRequestDay } from "./gapsOpenShiftsModel";

/** One response to a request, with the exceptions its line prints (P3-12). */
export type GapsApplicant = {
	/**
	 * The line's own key. The responses are the desktop's (`getRespondedRequestData`), not deduplicated, so a nurse on two
	 * response rows is two lines, as the Requests screen lists them — and needs a key the nurse id alone cannot give.
	 */
	rowKey: string;
	nurseId: number;
	/** As every roster in the app prints a person (`getNurseFormattedName`). */
	name: string;
	/** `undefined` for a responder no longer on the department's roster — still listed, since they did answer. */
	nurse: Nurse | undefined;
	response: RequestResponsesStatus;
	/** The job the chip names (`applicantJob`); `undefined` when the person holds none. */
	job: Job | undefined;
	/** The hours they accepted when they answered for PART of the shift (`isPartialShift`), else `null`. */
	partial: GapsAcceptedHours | null;
	/** Agency staff (`isAgencyNurse`: travel or per-diem) — the desktop approval's agency warning, as a mark. */
	isAgency: boolean;
	/** Their other shift that day — a double — or unknown for a day the phone has not loaded. */
	otherShift: GapsOtherShiftThatDay;
	/**
	 * The overtime approving them would add, from the backend's prediction for the hours they would work. `undefined`
	 * while it is not asked for (a collapsed card), loading, unknown — or for a partial pickup, whose own hours the
	 * batch cannot predict (see `useGapsApplicants`).
	 */
	overtimeHours: number | undefined;
};

/**
 * The responses to a request, one line per response row exactly as the desktop Requests screen lists and counts them
 * (`getRespondedRequestData`), each with what the approve decision turns on: a partial pickup, agency, a double, and
 * the overtime it adds.
 *
 * Overtime is the only part that costs a request — one batch prediction per request — so it is asked for only when the
 * caller says the answers are on screen (`withOvertime`: an expanded card, or one inside the 48h band), never for the
 * whole list at once. The batch predicts ONE window for every nurse it is given (`usePredictCostBatch` takes a single
 * start and end), so it is asked for the posted hours — the hours a whole-shift pickup would work. A partial pickup
 * works only part of them, and the backend predicts an approval on the ACCEPTED hours (`services/staffing.py`), so the
 * posted-hours figure would overstate their overtime: their line carries no overtime mark, and the confirm predicts on
 * their own hours before anything is written. So they are not asked about at all — only whole-shift pickups go in the
 * batch, and a request answered only by partial pickups asks for nothing.
 */
export const useGapsApplicants = (request: BaseRequest, withOvertime: boolean): { applicants: GapsApplicant[]; isOvertimeLoading: boolean } => {
	const { hospitalData, chosenDepartment, getNurseById, getNurseFormattedName } = useHospitalData();
	const { isPartialShift } = useRequestUtils();
	const responders = useMemo(() => getRespondedRequestData(request), [request]);
	// The people whose overtime the batch CAN say: those who would work the posted hours (see above).
	const wholeShiftNurseIds = useMemo(
		() => responders.filter(({ nurse_id }) => !isPartialShift(request, nurse_id)).map(({ nurse_id }) => nurse_id),
		[responders, isPartialShift, request],
	);
	const dayShifts = useMemo(() => shiftsOnRequestDay(hospitalData.weeks, request), [hospitalData.weeks, request]);

	const startISO = request.message.shift_start.toISO();
	const endISO = request.message.shift_end.toISO();
	const batchParams = useMemo(() => {
		if (!withOvertime || wholeShiftNurseIds.length === 0 || startISO === null || endISO === null) return null;
		return {
			departmentId: chosenDepartment.department_id,
			nurseIds: wholeShiftNurseIds,
			startTime: startISO,
			endTime: endISO,
			...(request.job_id === undefined ? {} : { jobId: request.job_id }),
		};
	}, [withOvertime, wholeShiftNurseIds, startISO, endISO, request.job_id, chosenDepartment.department_id]);
	// Everyone asked about in one page: a request's answers are a handful of people, and the line needs all of them at
	// once. The floor of one only matters while nobody is asked about, when the query is off anyway.
	const batch = usePredictCostBatch(batchParams, Math.max(wholeShiftNurseIds.length, 1));
	useReportPredictCostFailure(batch, {
		source: GapsPredictCostSource.OpenShifts,
		departmentId: chosenDepartment.department_id,
		nurseCount: wholeShiftNurseIds.length,
	});

	const applicants = useMemo<GapsApplicant[]>(
		() =>
			responders.map((response, index) => {
				const nurse = getNurseById(response.nurse_id);
				const isPartial = isPartialShift(request, response.nurse_id);
				// The batch's figure is for the posted hours, which a partial pickup does not work (see above).
				const prediction = batchParams === null || isPartial ? undefined : batch.predictions[response.nurse_id];
				return {
					rowKey: `${response.nurse_id}-${index}`,
					nurseId: response.nurse_id,
					name: getNurseFormattedName(response.nurse_id),
					nurse,
					response,
					job: nurse === undefined ? undefined : applicantJob(nurse, request.job_id),
					partial:
						isPartial && response.accepted_start_time !== undefined && response.accepted_end_time !== undefined
							? { start: response.accepted_start_time, end: response.accepted_end_time }
							: null,
					isAgency: nurse !== undefined && isAgencyNurse(nurse.employmentType),
					otherShift: otherShiftThatDay(dayShifts, response.nurse_id),
					overtimeHours:
						prediction === undefined ? undefined : buildCostPrediction(prediction, { isGlobalSalary: Boolean(nurse?.isGlobalSalary) }).overtimeHours,
				};
			}),
		[responders, getNurseById, getNurseFormattedName, isPartialShift, request, batchParams, batch.predictions, dayShifts],
	);

	return { applicants, isOvertimeLoading: batchParams !== null && batch.isLoading };
};
